import {IVOptionSetEndpoint, IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
import {PermitteeType} from "~/enums/permitteeType";
import {TagColor} from "~/enums/tagColor";
import {userActivationStatusDisplay} from "~/deactivatable";

export default {
  computed: {
    permitteeVOptionSets: {
      get(): IVOptionSetLocal[] {
        return [
          this.originalSubmitterVOptionSet,
          this.allApproversVOptionSet,
          this.orgGroupsVOptionSet,
          this.usersVOptionSet
        ];
      }
    },
    permitteeWithThirdPartyVOptionSets: {
      get(): IVOptionSetLocal[] {
        return [
          this.originalSubmitterVOptionSet,
          this.allCompletersVOptionSet,
          this.appAssignedOrgGroupsVOptionSet,
          this.appGroupsVOptionSet,
          this.thirdPartiesVOptionSet
        ];
      }
    },
    stepViewersVOptionSets: {
      get(): IVOptionSetLocal[] {
        return [
          this.originalSubmitterVOptionSet,
          ...this.permitteeCompletersVOptionSets,
          this.thirdPartiesVOptionSet
        ];
      }
    },
    permitteeCompletersVOptionSets: {
      get(): IVOptionSetLocal[] {
        return [
          this.stepCompletersVOptionSet,
          this.allCompletersVOptionSet,
          this.appGroupsVOptionSet,
          this.appAssignedOrgGroupsVOptionSet,
        ];
      }
    },
    usersAndGroupsVOptionSets:{
      get(): IVOptionSetLocal[] {
        return [
          this.orgGroupsVOptionSet,
          this.usersVOptionSet
        ];
      }
    },
    objectModelPermissionPermitteeVOptionSets: {
      get(): IVOptionSetLocal[] {
        return [
          this.anyoneVOptionSet,
          this.recordHolderVOptionSet,
          this.relatedObjectOwnerVOptionSet,
          this.accountRelationsVOptionSet,
          this.entityAccountRelationsVOptionSet,
          this.appGroupsVOptionSet,
          this.appAssignedOrgGroupsVOptionSet,
        ];
      }
    },
    objectModelActionPerformerVOptionSets: {
      get(): IVOptionSetLocal[] {
        return [
          this.recordHolderVOptionSet,
          this.relatedObjectOwnerVOptionSet,
          this.orgGroupsVOptionSet,
          this.usersVOptionSet,
        ];
      }
    },
    objectModelActionFormPermitteeVOptionSets: {
      get(): IVOptionSetLocal[] {
        return this.objectModelActionPerformerVOptionSets;
      }
    },
    objectModelTriggerNotificationVOptionSets: {
      get(): IVOptionSetLocal[] {
        return [
          this.recordHolderVOptionSet,
          this.relatedObjectOwnerVOptionSet,
          this.appAssignedOrgGroupsVOptionSet,
          this.appGroupsVOptionSet,
        ]
      }
    },
    appScopedGroupsVOptionSets: {
      get(): IVOptionSetEndpoint[] {
        return [
          this.appGroupsVOptionSet,
          this.appAssignedOrgGroupsVOptionSet,
        ]
      }
    },
    originalSubmitterVOptionSet: {
      get(): IVOptionSetLocal {
        return {
          optionType: PermitteeType.originalSubmitter,
          options: [{label: 'Original Submitter', value: PermitteeType.originalSubmitter.toString()}],
          isPinned: true,
          selectedColor: TagColor[PermitteeType.originalSubmitter]
        };
      }
    },
    entityOwnerVOptionSet: {
      get(): IVOptionSetLocal {
        return {
          optionType: PermitteeType.entityOwner,
          options: [{label: 'Entity Owner', value: PermitteeType.entityOwner.toString()}],
          isPinned: true,
          selectedColor: TagColor[PermitteeType.entityOwner]
        };
      }
    },
    allApproversVOptionSet: {
      get(): IVOptionSetLocal {
        return {
          optionType: PermitteeType.allApprovers,
          options: [{label: 'All Approvers', value: PermitteeType.allApprovers.toString()}],
          isPinned: true,
          selectedColor: TagColor[PermitteeType.allApprovers]
        };
      }
    },
    allCompletersVOptionSet: {
      get(): IVOptionSetLocal {
        return {
          optionType: PermitteeType.allCompleters,
          options: [{label: 'All Completers', value: PermitteeType.allCompleters.toString()}],
          isPinned: true,
          selectedColor: TagColor[PermitteeType.allCompleters]
        };
      }
    },
    stepCompletersVOptionSet: {
      get(): IVOptionSetLocal {
        return {
          optionType: PermitteeType.stepCompleters,
          options: [{label: 'Current Step Completers', value: PermitteeType.stepCompleters.toString()}],
          isPinned: true,
          selectedColor: TagColor[PermitteeType.stepCompleters]
        };
      }
    },
    orgGroupsVOptionSet: {
      get(): IVOptionSetEndpoint {
        return {
          optionType: PermitteeType.group,
          heading: 'Groups',
          valueKey: 'id',
          labelKey: 'name',
          endpoint: `/organizations/${this.selectedOrganization.id}/search_groups`,
          rootKey: 'groups',
          selectedColor: TagColor[PermitteeType.group]
        };
      }
    },
    appGroupsVOptionSet: {
      get(): IVOptionSetEndpoint {
        return {
          optionType: PermitteeType.group,
          heading: 'App Groups',
          valueKey: 'id',
          labelKey: 'name',
          endpoint: `/apps/${this.application_id}/search_groups`,
          endpointFilters: { organization_id: this.selectedOrganization.id },
          rootKey: 'app_groups',
          selectedColor: TagColor[PermitteeType.group]
        };
      }
    },
    appAssignedOrgGroupsVOptionSet: {
      get(): IVOptionSetEndpoint {
        return {
          optionType: PermitteeType.group,
          heading: 'Org Groups',
          valueKey: 'id',
          labelKey: 'name',
          endpoint: `/apps/${this.application_id}/search_groups`,
          endpointFilters: { organization_id: this.selectedOrganization.id },
          rootKey: 'org_groups',
          selectedColor: TagColor[PermitteeType.group]
        };
      }
    },
    usersVOptionSet: {
      get(): IVOptionSetEndpoint {
        return {
          optionType: PermitteeType.user,
          heading: 'Users',
          valueKey: 'id',
          labelKey: (user) => `${userActivationStatusDisplay(user)}`,
          endpoint: `/organizations/${!!this.form ? this.form.organization_id : this.$store.state.selected.organization.id}/search_users`,
          rootKey: 'users',
          selectedColor: TagColor[PermitteeType.user]
        };
      }
    },
    thirdPartiesVOptionSet: {
      get(): IVOptionSetLocal {
        return {
          optionType: PermitteeType.thirdParty,
          heading: 'Third Parties',
          valueKey: 'client_id',
          labelKey: 'referenceNameOrDefault',
          options: this.selectedWorkflowStep?.referenceableThirdPartyElements,
          selectedColor: TagColor[PermitteeType.thirdParty]
        };
      }
    },
    lookupFieldVOptionSet: {
      get(): IVOptionSetLocal {
        return {
          optionType: PermitteeType.lookupField,
          heading: 'Lookup Records',
          valueKey: 'client_id',
          labelKey: 'referenceNameOrDefault',
          options: this.selectedWorkflowStep?.referenceableLookupObjectModelFields,
          selectedColor: TagColor[PermitteeType.lookupField]
        };
      }
    },
    anyoneVOptionSet: {
      get(): IVOptionSetLocal {
        return {
          optionType: PermitteeType.anyone,
          options: [{label: 'Anyone', value: PermitteeType.anyone.toString()}],
          isPinned: true,
          selectedColor: TagColor[PermitteeType.anyone],
        };
      }
    },
    recordHolderVOptionSet: {
      get(): IVOptionSetLocal {
        return {
          optionType: PermitteeType.objectOwner,
          options: [{label: 'Record Holder', value: PermitteeType.objectOwner.toString()}],
          isPinned: true,
          selectedColor: TagColor[PermitteeType.objectOwner],
        };
      }
    },
    relatedObjectOwnerVOptionSet: {
      get(): IVOptionSetLocal {
        const options = [];
        for (const relation of this.selectedObjectModel.modelRelationsArray) {
          options.push({label: relation.inverseLabelTitleDisplay, client_id: relation.client_id});
        }
        for (const relation of this.selectedObjectModel.inverse_object_model_relations.all) {
          options.push({label: relation.labelTitleDisplay, client_id: relation.client_id});
        }
        return {
          optionType: PermitteeType.relatedObjectOwner,
          valueKey: 'client_id',
          heading: 'Related Record Holders',
          options,
          isPinned: true,
          selectedColor: TagColor[PermitteeType.relatedObjectOwner],
        };
      }
    },
    accountRelationsVOptionSet: {
      get(): IVOptionSetLocal {
        const options = [];
        for (const accountRelation of this.selectedObjectModel.accountRelationsArray) {
          options.push({label: accountRelation.permissionDisplay, client_id: accountRelation.client_id});
        }

        return {
          optionType: PermitteeType.accountRelation,
          valueKey: 'client_id',
          heading: 'Accounts',
          options,
          selectedColor: TagColor[PermitteeType.accountRelation],
        }
      }
    },
    entityAccountRelationsVOptionSet: {
      get(): IVOptionSetLocal {
        const options = [];
        for (const entityAccountRelation of this.selectedObjectModel.entityAccountRelationsArray) {
          const relationName = entityAccountRelation.permissionDisplay;
          options.push({label: `${relationName} (owner)`, value: `${entityAccountRelation.client_id}-owner`});
          options.push({label: `${relationName} (member)`, value: `${entityAccountRelation.client_id}-member`});
        }

        return {
          optionType: PermitteeType.entityAccountRelation,
          heading: 'Entity Accounts',
          options,
          selectedColor: TagColor[PermitteeType.entityAccountRelation],
        }
      }
    },

    // SecurityMaskable
    permitteeSecurityMaskableFormElementViewersVOptionSets: {
      get(): IVOptionSetLocal[] {
        return [
          this.originalSubmitterVOptionSet,
          this.stepCompletersVOptionSet,
          this.allCompletersVOptionSet,
          this.appGroupsVOptionSet,
          this.appAssignedOrgGroupsVOptionSet,
          this.thirdPartiesVOptionSet,
        ]
      }
    },
    permitteeSecurityMaskableObjectModelViewersVOptionSets: {
      get(): IVOptionSetLocal[] {
        return [
          this.recordHolderVOptionSet,
          this.appGroupsVOptionSet,
          this.appAssignedOrgGroupsVOptionSet,
        ]
      }
    },
  }
};